import axios from "axios";
import Cookie from "js-cookie";
import _ from "lodash";
import Vue from "vue";

// initial state
const state =
{
	sessions: { },
	activeSessions: { }
}

// getters
const getters =
{
	// Get a list (array) of all stored sessions
	sessionList: ( state ) =>
	{
		return Object.values( state.sessions );
	},
	// Get a basic LOOKUP of sessions keyed by Session-GUID (<node-id>/<raw-session-key>)
	sessionLookup: ( state ) =>
	{
		return _.clone( state.sessions );
	},
	// Get a 2-level LOOKUP of sessions organized by Node-ID, and then raw Session-Key within that node.
	sessionLookupByNode: ( state ) =>
	{
		let lookup = { };
		for( let sid in state.sessions )
		{
			let parts = sid.split( '/' );
			if( parts.length !== 2 )
				console.error( "Invalid session key found: "+ sid +"\nFORMAT: Session keys must be stored in the form \"<node-id>/<raw-session-key>\"\nSkipping this session.." );
			else
			{
				let rawSessionKey = parts[1];
				let sessionNode = parts[0];
				if( !lookup.hasOwnProperty( sessionNode ) )
					lookup[sessionNode] = { };
				lookup[sessionNode][rawSessionKey] = state.sessions[sid];
			}
		}
		return lookup;
	},
	// Get the active session GUID for the given node. rawKey = true will give the KEY, not GUID.
	activeSession: ( state ) => ( nodeId, rawKey = false ) =>
	{
		if( !nodeId )
			throw new TypeError( "You must provide a Node ID for which to retrieve the active session." );
		let guid = state.activeSessions[nodeId];
		if( !guid )
			return null;
		else if( rawKey )
			return guid.substr( guid.indexOf('/') + 1 );
		else
			return guid;
	},
	// Get a lookup of all active sessions, keyed by Node ID
	activeSessionLookup: ( state ) =>
	{
		return _.clone( state.activeSessions );
	},
	// Get details of session by session-GUID
	session: ( state ) => ( sessionGuid ) =>
	{
		return state.sessions[sessionGuid] || null;
	}
}

// actions
const actions =
{
	async login( { state, getters, commit, dispatch }, { nodeId = null, account, username, password } )
	{
		await dispatch( "logout", { } );

		// TODO: Use nodeId to log into that node...
		let homeNode = await dispatch( 'nodes/getHomeNode', { }, { root : true } );
		
		nodeId = nodeId || homeNode.$id;

		let proxyPrefix = "";
		if( nodeId != homeNode.$id )
			proxyPrefix = "/proxy/"+ nodeId;

		console.log( "Logging into node:", nodeId );

		try {
			// TODO: Set the full URL (or proxy path) for the node-specific access
			let result = await axios( { method: "POST", url: process.env.APIHOST + proxyPrefix +"/api/v1/authentication", data : { account, username, password } } );
			let newSession = result.data.$data;
			newSession._node = nodeId;	// Set node for internal use.
			commit( 'addSession', newSession )
			//commit( 'setActiveSession', nodeId +"/"+ newSession.$id );
			return newSession;
		} catch( err ) {
			if( err.response && err.response.status === 401 )
				return null;	// Auth failed.
			throw err;
		}
	},
	async logout( { commit, state, getters, dispatch }, { nodeId = null, sessionKey = null } )
	{
		if( sessionKey )
		{
			let sParts = sessionKey.split( "/" );
			if( sParts.length > 1 )
			{
				if( sParts[0] && nodeId && sParts[0] != nodeId )
					throw new Error( "There is a conflict regarding which Node ID. The nodeId argument ("+ nodeId +") does not match the Node ID of the Session GUID provided ("+ sessionKey +")" );
				nodeId = sParts[0];
				sessionKey = sParts[1];
			}
		}

		let homeNode = await dispatch( 'nodes/getHomeNode', { }, { root : true } );

		if( !nodeId )
			nodeId = homeNode.$id;

		if( !sessionKey )
			sessionKey = getters.activeSession( nodeId, true );	// Get the raw session key of the relevant node.

		if( !sessionKey )
			return false;	// Nothing to log out.

		let sessionGuid = nodeId +"/"+ sessionKey;

		if( !state.sessions.hasOwnProperty( sessionGuid ) )
			console.warn( "This session has not been registered.\nSession GUID: "+ sessionGuid );

		let proxyPrefix = "";
		if( nodeId && nodeId != homeNode.$id )	// If we're not logging out of the home node, add the appropriate proxy prefix to hit the right Node.
			proxyPrefix = "/proxy/"+ nodeId;

		try {
			await axios( { method: "DELETE", url: process.env.APIHOST + proxyPrefix +"/api/v1/sessions/" + sessionKey });
			commit( 'removeSession', sessionGuid );
			return true;
		} catch( err ) {
			commit( 'removeSession', sessionGuid );
			if( err.response && err.response.status === 404 )
				return false;	// Nothing to log out.
			throw err;
		}
	},
	async checkSession( { state, getters, commit, dispatch }, { nodeId = null, sessionKey = null } )
	{
		if( sessionKey )
		{
			let sParts = sessionKey.split( "/" );
			if( sParts.length > 1 )
			{
				if( sParts[0] && nodeId && sParts[0] != nodeId )
					throw new Error( "There is a conflict regarding which Node ID. The nodeId argument ("+ nodeId +") does not match the Node ID of the Session GUID provided ("+ sessionKey +")" );
				nodeId = sParts[0];
				sessionKey = sParts[1];
			}
		}

		let homeNode = await dispatch( 'nodes/getHomeNode', { }, { root : true } );

		if( !nodeId )
			nodeId = homeNode.$id;

		if( !sessionKey )
			sessionKey = getters.activeSession( nodeId, true );	// Get the raw session key of the relevant node.

		if( !sessionKey )
			return false;	// Nothing to log out.

		let sessionGuid = nodeId +"/"+ sessionKey;

		if( !state.sessions.hasOwnProperty( sessionGuid ) )
			console.warn( "This session has not been registered.\nSession GUID: "+ sessionGuid );

		let proxyPrefix = "";
		if( nodeId && nodeId != homeNode.$id )	// If we're not logging out of the home node, add the appropriate proxy prefix to hit the right Node.
			proxyPrefix = "/proxy/"+ nodeId;

		let options = { activeonly : "1" };

		try {
			// TODO: Set the full URL (or proxy path) for the node-specific access
			let result = await axios( { method: "GET", query : options, url: process.env.APIHOST + proxyPrefix +"/api/v1/sessions/"+ sessionKey, headers : { 'wn-session-key' : sessionKey } } );
			let session = result.data.$data || null;
			session._node = nodeId;
			commit( 'addSession', session );
			return session;
		} catch( err ) {
			if( !err.response || [401, 403, 404].indexOf( err.response.status ) === -1 )
				throw err;
			commit( 'removeSession', sessionGuid );
			return null;
		}
	}
}

// mutations
const mutations =
{
	// Set the active session
	// WHY ask for nodeId AND session GUID (which has its nodeID)? Because at some point we will allow cross-node sessions (and master sessions)
	setActiveSession( state, { nodeId, sessionGuid } )
	{
		if( !nodeId || typeof nodeId !== 'string' ) throw new TypeError( "Must provide a [nodeId] STRING argument!" );
		
		if( sessionGuid )
		{
			if( typeof sessionGuid !== 'string' )
				throw new Error( "Must provide a STRING Session GUID!\nFormat: \"<node-id>/<raw-session-key>\"" );
			
			let parts = sessionGuid.split( '/' );
			if( parts.length != 2 )
				throw new Error( "Invalid Session GUID provided ("+ sessionGuid +")\nFORMAT: Session keys must be stored in the form \"<node-id>/<raw-session-key>\"" );
			let session = state.sessions[sessionGuid];
			if( !session )
				throw new Error( "The session GUID provided ("+ sessionGuid +") is not registered, so it cannot be set as an active session." );

			Vue.set( state.activeSessions, nodeId, sessionGuid );
		}
		else
		{
			Vue.delete( state.activeSessions, nodeId );
		}
	},
	// Add a session to the list. Does NOT set the session as ACTIVE; see setSession for that.
	addSession( state, newSession )
	{
		if( !newSession || typeof newSession !== 'object' || !newSession.$id )
			throw new Error( "Must provide a session object!" );
		if( !newSession._node )
			throw new Error( "When setting a new session, you must set the _node field with the Node ID!" );
		
		let sessionGuid = newSession._node +"/"+ newSession.$id;
		
		//if( state.sessions[sessionGuid] )
		//	console.warn( "authentication.addSession: Adding a session that already exists! The previous session data is being overridden." );

		Vue.set( state.sessions, sessionGuid, newSession );
	},
	// Set the ACTIVE session
	setSession( state, sessionGuid )
	{
		if( sessionGuid )
		{
			if( typeof sessionGuid !== 'string' )
				throw new Error( "Must provide a STRING Session GUID!\nFormat: \"<node-id>/<raw-session-key>\"" );
			
			let parts = sessionGuid.split( '/' );
			if( parts.length != 2 )
				throw new Error( "Invalid Session GUID provided ("+ sessionGuid +")\nFORMAT: Session keys must be stored in the form \"<node-id>/<raw-session-key>\"" );
			let session = state.sessions[sessionGuid];
			if( !session )
				throw new Error( "The session GUID provided ("+ sessionGuid +") is not registered, so it cannot be set as the active session." );
			state.session = session;
			Cookie.set( 'wn-ui-session-key', sessionGuid );
			axios.defaults.headers.common['wn-session-key'] = session.$id;
		}
		else
		{
			Cookie.remove( 'wn-ui-session-key' );
			delete axios.defaults.headers.common['wn-session-key'];
			state.session = null;
		}
	},
	// Remove the session under the given GUID. If it is active, also clear the active session.
	removeSession( state, sessionGuid )
	{
		let parts = sessionGuid.split( '/' );
		if( parts.length != 2 )
			throw new Error( "authentication.removeSession: Invalid Session GUID provided ("+ sessionGuid +")\nFORMAT: Session keys must be stored in the form \"<node-id>/<raw-session-key>\"" );
		
		let sessionKey = parts[1];
		let sessionNode = parts[0];

		for( let n in state.activeSessions )
		{
			if( state.activeSessions[n] == sessionGuid )
				Vue.delete( state.activeSessions, n );
		}
		Vue.delete( state.sessions, sessionGuid );
	}
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/authentication.js